import { AlertTriangle, Info, Power } from "lucide-react";
import { useMemo } from "react";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaTextareaField } from "@/shared/components/forms/ColocaTextareaField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { formatRail } from "@/shared/utils/railDisplay";

import { useRailMutations } from "../../hooks/useRailMutations";
import type { RailAvailabilityCell } from "../../types/paymentRails.types";

export interface DisableRailIntent {
  cell: RailAvailabilityCell;
  /** Scope being written: null = the global switch, otherwise that organization's override. */
  organizationId: string | null;
  organizationName?: string;
}

export interface DisableRailDialogProps {
  intent: DisableRailIntent;
  open: boolean;
  onClose: () => void;
  onDisabled: (railLabel: string) => void;
  /** Organization names to spell out which overrides survive a global disable. */
  orgNameById: Map<string, string>;
}

interface FormValues {
  reason: string;
}

/**
 * The disable confirmation — enabling never comes through here, it flips directly from the switch. Demands a
 * reason (the backend refuses without one — the row is the audit trail) and spells out the consequence:
 * creation of payins/payouts/quotes on the rail answers 400, while in-flight operations and their settlement
 * webhooks are untouched. A GLOBAL disable additionally lists the organizations whose `enabled: true`
 * override will keep them operating — most-specific-wins means the global switch does not beat them.
 */
export function DisableRailDialog({
  intent,
  open,
  onClose,
  onDisabled,
  orgNameById,
}: DisableRailDialogProps) {
  const { t } = useTranslations("paymentRails");
  const { isSubmitting, error, setRailState } = useRailMutations();
  const { cell, organizationId, organizationName } = intent;

  const schema = useMemo(
    () => z.object({ reason: z.string().trim().min(1, t("reason_required")).max(500) }),
    [t],
  );
  const form = useZodForm(schema, { reason: "" });

  const railLabel = formatRail(cell.rail);
  const directionLabel = t(cell.direction === "PAYIN" ? "direction_payin" : "direction_payout");

  // Only overrides that keep the rail ON survive a global disable in a way worth warning about.
  const survivingOrgs =
    organizationId === null
      ? cell.org_overrides
          .filter((override) => override.enabled)
          .map((override) => orgNameById.get(override.organization_id) ?? override.organization_id)
      : [];

  const onSubmit = async (values: FormValues) => {
    const failure = await setRailState(cell.rail, {
      direction: cell.direction,
      organization_id: organizationId,
      enabled: false,
      reason: values.reason.trim(),
    });
    if (!failure) {
      form.reset({ reason: "" });
      onDisabled(railLabel);
    }
  };

  return (
    <ColocaDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isSubmitting) {
          form.reset({ reason: "" });
          onClose();
        }
      }}
      title={t("toggle_title_disable", { rail: railLabel })}
      description={t(organizationId ? "toggle_scope_org" : "toggle_scope_global")}
    >
      <ColocaForm form={form} onSubmit={onSubmit} className="space-y-4">
        <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-border bg-card p-3">
          <ColocaBadge variant="secondary" className="font-sans">
            {railLabel}
          </ColocaBadge>
          <ColocaBadge variant="default" className="font-sans">
            {directionLabel}
          </ColocaBadge>
          <span className="ml-auto text-xs text-muted-foreground">
            {organizationId ? (organizationName ?? organizationId) : t("scope_global_label")}
          </span>
        </div>

        <ColocaAlert variant="warning" icon={AlertTriangle} text={t("toggle_consequence_disable")} />

        {survivingOrgs.length > 0 && (
          <ColocaAlert
            variant="secondary"
            icon={Info}
            heading={t("toggle_surviving_overrides_title")}
            text={`${t("toggle_surviving_overrides_text")} ${survivingOrgs.join(", ")}`}
          />
        )}

        <ColocaTextareaField
          name="reason"
          label={t("reason_label")}
          placeholder={t("reason_placeholder")}
          description={t("reason_internal_hint")}
          maxLength={500}
          rows={3}
        />

        {error && <ColocaAlert variant="destructive" text={error} />}

        <div className="flex justify-end gap-2 pt-2">
          <ColocaButton type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
            {t("toggle_cancel")}
          </ColocaButton>
          <ColocaButton type="submit" variant="destructive" icon={Power} loading={isSubmitting}>
            {t("toggle_confirm_disable")}
          </ColocaButton>
        </div>
      </ColocaForm>
    </ColocaDialog>
  );
}
